export type RulesEngineTriggerTarget = 'account' | 'contact' | 'both';

export interface RulesEngineTrigger {
  id: string;
  name: string;
  enabled: boolean;
  target: RulesEngineTriggerTarget;
  blockedStages: string[];
}

export interface WarmupDeletionTierBreakdown {
  newMailboxRetentionCount: number;
  matureMailboxRetentionCount: number;
  deletedNewMailboxTier: number;
  deletedMatureMailboxTier: number;
}

// Mirrors the backend SentByEmailKindBreakdown shape (see
// src/rules-engine/warmup-retention-worker.ts on the API). Kept as a
// pure round-trip type for now — no UI surface; the FE just needs to
// preserve these counters across save/refetch so the new config flag
// `WarmupDeletionConfig.includeSentByEmailKind` is not silently
// stripped by `normalizeRulesEngineConfig` on save.
export interface WarmupDeletionSentByEmailKindBreakdown {
  scannedMailboxes: number;
  scannedSentRows: number;
  candidateSentRows: number;
  deletedSentRows: number;
  abortedMailboxesByCap: number;
  abortedMailboxesByRatio: number;
}

export interface WarmupDeletionRunSummary {
  startedAt?: string;
  finishedAt?: string;
  status?: 'success' | 'partial' | 'failed' | 'skipped' | 'running';
  dryRun: boolean;
  targetFolder: string;
  processedMailboxes: number;
  totalMailboxes: number;
  deletedEmails: number;
  candidateEmails: number;
  errors: number;
  durationMs: number;
  tierBreakdown: WarmupDeletionTierBreakdown;
  sentByEmailKindBreakdown: WarmupDeletionSentByEmailKindBreakdown;
  notes?: string;
}

export interface WarmupDeletionRuntimeState {
  runDate: string;
  cursor: number;
  totalMailboxes: number;
  processedMailboxes: number;
  deletedEmails: number;
  candidateEmails: number;
  errors: number;
  startedAt: string;
  lastHeartbeatAt?: string;
  dryRun: boolean;
  targetFolder: string;
  status: 'running' | 'completed';
  tierBreakdown: WarmupDeletionTierBreakdown;
  sentByEmailKindBreakdown: WarmupDeletionSentByEmailKindBreakdown;
}

export interface WarmupDeletionConfig {
  enabled: boolean;
  dryRun: boolean;
  targetFolder: string;
  targetFolderAliases: string[];
  scheduleTimeUtc: string; // HH:mm wall-clock in selected scheduleTimezone (legacy field name kept for compatibility)
  scheduleTimezone: string; // IANA timezone, e.g. UTC or America/New_York
  mailboxAgeThresholdDays: number; // 30
  retentionDaysForNewMailbox: number; // 30
  retentionDaysForMatureMailbox: number; // 14
  maxConcurrentMailboxes: number; // safety cap
  maxMailboxesPerMinute: number; // global start-rate throttle
  deleteBatchSize: number; // candidate grouping before write chunks
  // Sent-by-emailKind retention path. Strict-true-only opt-in: any
  // value other than literal `true` keeps it OFF. Round-tripped
  // losslessly here so the FE save flow does NOT silently strip the
  // backend-set flag. No UI surface yet — operators set this directly
  // on the rules-engine config row.
  includeSentByEmailKind: boolean;
  sentMaxDeletePerMailboxPerRun: number; // per-mailbox cap, clamped 25..50000
  sentSuspiciousRatioAbort: number; // ratio in [0,1]; default 0.95
  sentMinScanForRatioCheck: number; // min Sent rows before ratio guard fires
}

export interface DelayBounceSuppressionConfig {
  enabled: boolean;
  suppressAfterDelayBounces24h: number; // trigger suppression when count hit in last 24h
  suppressAfterDelayBounces48h: number; // trigger suppression when count hit in last 48h
  retryAfterDays: number; // suppression cool-down before retries are allowed again
  retryMode: 'after_days' | 'never';
}

export interface HardBounceSuppressionConfig {
  enabled: boolean;
  suppressAfterHardBounces: number;
  scope: 'mailbox' | 'global';
}

export interface BounceClassificationConfig {
  // Tenant-level activation intent (covers all tenant domains/mailboxes).
  enabled: boolean;
  activationScope: 'tenant';
  // Effective runtime state after backend safety gates.
  effectiveEnabled: boolean;
  // Backward-compatible alias for older payloads.
  v1Enabled?: boolean;
}

// Phase A (2026-05-13): per-tenant Warm-Up Identifier. Mirrors the
// API-side type in `kross-global-marketing-api :: src/lib/tenant-warmup-identifier.ts`.
//
// PERSISTED SHAPE (rich): all five fields round-trip through save/refetch.
// UI SHAPE (Phase A): the Rules Engine Card exposes only `enabled` +
// `pattern`. `matchScope` / `matchType` / `caseSensitive` persist with
// system defaults so a future UI release adding advanced controls does
// not require a schema migration.
export type WarmupIdentifierMatchScope = 'subject' | 'body' | 'subject_or_body';
export type WarmupIdentifierMatchType = 'contains' | 'regex';

export interface WarmupIdentifierConfig {
  enabled: boolean;
  pattern: string;
  matchScope: WarmupIdentifierMatchScope;
  matchType: WarmupIdentifierMatchType;
  caseSensitive: boolean;
}

export type HardBounceRuntimeStatus = 'disabled' | 'enabled' | 'active';

export interface DelayBounceSuppressionSummary {
  tenantId: string;
  lastSuppressedAt?: string;
  lastSuppressedEventAt?: string;
  lastSuppressedRecipient?: string;
  updatedAt?: string;
}

export interface RulesEngineConfig {
  accountStages: string[];
  contactStages: string[];
  triggers: RulesEngineTrigger[];
  warmupDeletion: WarmupDeletionConfig;
  // Phase A (2026-05-13): per-tenant Warm-Up Identifier. Sibling to
  // `warmupDeletion`. ALWAYS present after `normalizeRulesEngineConfig`
  // (defaults to disabled with empty pattern), so the UI can read it
  // unconditionally. Round-tripped losslessly so the FE save flow does
  // NOT silently strip backend-set fields.
  warmupIdentifier: WarmupIdentifierConfig;
  delayBounceSuppression: DelayBounceSuppressionConfig;
  hardBounceSuppression: HardBounceSuppressionConfig;
  bounceClassification: BounceClassificationConfig;
  delayBounceSuppressionSummary?: DelayBounceSuppressionSummary;
  warmupDeletionLastRun?: WarmupDeletionRunSummary;
  warmupDeletionRuntime?: WarmupDeletionRuntimeState;
  updatedAt?: string;
  updatedBy?: string;
}

export const DEFAULT_ACCOUNT_STAGES: string[] = [
  'Prospecting',
  'Active Opportunity',
  'Dead Opportunity',
  'Do Not Prospect',
];

export const DEFAULT_CONTACT_STAGES: string[] = [
  'Cold',
  'Warm',
  'Hot',
  'Dead Opportunity',
  'Do Not Prospect',
];

export const DEFAULT_RULES_ENGINE_TRIGGERS: RulesEngineTrigger[] = [
  {
    id: 'trigger-account-stop-dead-or-dnp',
    name: 'Block Account Emails: Dead Opportunity / Do Not Prospect',
    enabled: true,
    target: 'account',
    blockedStages: ['Dead Opportunity', 'Do Not Prospect'],
  },
  {
    id: 'trigger-contact-stop-dead-or-dnp',
    name: 'Block Contact Emails: Dead Opportunity / Do Not Prospect',
    enabled: true,
    target: 'contact',
    blockedStages: ['Dead Opportunity', 'Do Not Prospect'],
  },
];

export const DEFAULT_WARMUP_DELETION_TIER_BREAKDOWN: WarmupDeletionTierBreakdown = {
  newMailboxRetentionCount: 0,
  matureMailboxRetentionCount: 0,
  deletedNewMailboxTier: 0,
  deletedMatureMailboxTier: 0,
};

export const DEFAULT_WARMUP_DELETION_SENT_BY_EMAIL_KIND_BREAKDOWN: WarmupDeletionSentByEmailKindBreakdown = {
  scannedMailboxes: 0,
  scannedSentRows: 0,
  candidateSentRows: 0,
  deletedSentRows: 0,
  abortedMailboxesByCap: 0,
  abortedMailboxesByRatio: 0,
};

export const DEFAULT_WARMUP_DELETION_CONFIG: WarmupDeletionConfig = {
  enabled: false,
  dryRun: true,
  targetFolder: '',
  targetFolderAliases: [],
  scheduleTimeUtc: '03:30',
  scheduleTimezone: 'UTC',
  mailboxAgeThresholdDays: 30,
  retentionDaysForNewMailbox: 30,
  retentionDaysForMatureMailbox: 14,
  maxConcurrentMailboxes: 5,
  maxMailboxesPerMinute: 10,
  deleteBatchSize: 200,
  includeSentByEmailKind: false,
  sentMaxDeletePerMailboxPerRun: 1000,
  sentSuspiciousRatioAbort: 0.95,
  sentMinScanForRatioCheck: 100,
};

export const DEFAULT_DELAY_BOUNCE_SUPPRESSION_CONFIG: DelayBounceSuppressionConfig = {
  enabled: false,
  suppressAfterDelayBounces24h: 2,
  suppressAfterDelayBounces48h: 3,
  retryAfterDays: 7,
  retryMode: 'after_days',
};

export const DEFAULT_HARD_BOUNCE_SUPPRESSION_CONFIG: HardBounceSuppressionConfig = {
  enabled: true,
  suppressAfterHardBounces: 1,
  scope: 'global',
};

export const DEFAULT_BOUNCE_CLASSIFICATION_CONFIG: BounceClassificationConfig = {
  enabled: false,
  activationScope: 'tenant',
  effectiveEnabled: false,
  v1Enabled: false,
};

// Phase A (2026-05-13): default for per-tenant Warm-Up Identifier.
// Disabled + empty pattern means "no classification stamping" — the
// classifier falls back to the existing hard-coded vendor signals.
// `matchScope`/`matchType`/`caseSensitive` carry system defaults so
// future advanced UI controls don't require schema migration.
export const DEFAULT_WARMUP_IDENTIFIER_CONFIG: WarmupIdentifierConfig = {
  enabled: false,
  pattern: '',
  matchScope: 'subject_or_body',
  matchType: 'contains',
  caseSensitive: false,
};

export const DEFAULT_RULES_ENGINE_CONFIG: RulesEngineConfig = {
  accountStages: DEFAULT_ACCOUNT_STAGES,
  contactStages: DEFAULT_CONTACT_STAGES,
  triggers: DEFAULT_RULES_ENGINE_TRIGGERS,
  warmupDeletion: DEFAULT_WARMUP_DELETION_CONFIG,
  warmupIdentifier: DEFAULT_WARMUP_IDENTIFIER_CONFIG,
  delayBounceSuppression: DEFAULT_DELAY_BOUNCE_SUPPRESSION_CONFIG,
  hardBounceSuppression: DEFAULT_HARD_BOUNCE_SUPPRESSION_CONFIG,
  bounceClassification: DEFAULT_BOUNCE_CLASSIFICATION_CONFIG,
};

export function normalizeStageList(stages: unknown, fallback: string[] = []): string[] {
  const source = Array.isArray(stages) ? stages : fallback;
  const deduped = new Map<string, string>();

  source.forEach((entry) => {
    const stage = String(entry || '').trim();
    const key = stage.toLowerCase();
    if (!stage || deduped.has(key)) return;
    deduped.set(key, stage);
  });

  return Array.from(deduped.values());
}

function normalizeTarget(value: unknown): RulesEngineTriggerTarget {
  const target = String(value || '').trim().toLowerCase();
  if (target === 'account' || target === 'contact' || target === 'both') {
    return target;
  }
  return 'both';
}

function normalizePositiveInt(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(parsed)));
}

// Float in [min, max]; mirrors backend `normalizeRatio` so the
// suspicious-ratio gate round-trips losslessly.
function normalizeRatio(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function normalizeScheduleTimeUtc(value: unknown, fallback: string): string {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return fallback;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) {
    return fallback;
  }
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function isValidIanaTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function normalizeScheduleTimezone(value: unknown, fallback: string): string {
  const timezone = String(value || '').trim();
  if (!timezone) return fallback;
  return isValidIanaTimezone(timezone) ? timezone : fallback;
}

function normalizeTierBreakdown(input: unknown): WarmupDeletionTierBreakdown {
  const raw = (input && typeof input === 'object') ? (input as Record<string, unknown>) : {};
  return {
    newMailboxRetentionCount: normalizePositiveInt(raw.newMailboxRetentionCount, 0, 0, 1_000_000_000),
    matureMailboxRetentionCount: normalizePositiveInt(raw.matureMailboxRetentionCount, 0, 0, 1_000_000_000),
    deletedNewMailboxTier: normalizePositiveInt(raw.deletedNewMailboxTier, 0, 0, 1_000_000_000),
    deletedMatureMailboxTier: normalizePositiveInt(raw.deletedMatureMailboxTier, 0, 0, 1_000_000_000),
  };
}

function normalizeSentByEmailKindBreakdown(input: unknown): WarmupDeletionSentByEmailKindBreakdown {
  const raw = (input && typeof input === 'object') ? (input as Record<string, unknown>) : {};
  return {
    scannedMailboxes: normalizePositiveInt(raw.scannedMailboxes, 0, 0, 1_000_000_000),
    scannedSentRows: normalizePositiveInt(raw.scannedSentRows, 0, 0, 1_000_000_000),
    candidateSentRows: normalizePositiveInt(raw.candidateSentRows, 0, 0, 1_000_000_000),
    deletedSentRows: normalizePositiveInt(raw.deletedSentRows, 0, 0, 1_000_000_000),
    abortedMailboxesByCap: normalizePositiveInt(raw.abortedMailboxesByCap, 0, 0, 1_000_000_000),
    abortedMailboxesByRatio: normalizePositiveInt(raw.abortedMailboxesByRatio, 0, 0, 1_000_000_000),
  };
}

function normalizeFolderName(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s*:\s*/g, ':')
    .replace(/\s+/g, ' ');
}

function normalizeTargetFolderAliases(rawAliases: unknown, targetFolder: string): string[] {
  const source = Array.isArray(rawAliases) ? rawAliases : [];
  const targetKey = normalizeFolderName(targetFolder);
  const dedup = new Map<string, string>();
  source.forEach((entry) => {
    const alias = String(entry || '').trim();
    const key = normalizeFolderName(alias);
    if (!alias || !key || key === targetKey || dedup.has(key)) return;
    if (!/^warm ?up/.test(key)) return;
    dedup.set(key, alias);
  });
  return Array.from(dedup.values()).slice(0, 20);
}

function normalizeWarmupDeletionConfig(input: unknown): WarmupDeletionConfig {
  const raw = (input && typeof input === 'object')
    ? (input as Record<string, unknown>)
    : {};
  const targetFolder =
    String(raw.targetFolder || DEFAULT_WARMUP_DELETION_CONFIG.targetFolder).trim() ||
    DEFAULT_WARMUP_DELETION_CONFIG.targetFolder;
  return {
    enabled: raw.enabled === true,
    dryRun: raw.dryRun !== false,
    targetFolder,
    targetFolderAliases: normalizeTargetFolderAliases(raw.targetFolderAliases, targetFolder),
    scheduleTimeUtc: normalizeScheduleTimeUtc(raw.scheduleTimeUtc, DEFAULT_WARMUP_DELETION_CONFIG.scheduleTimeUtc),
    scheduleTimezone: normalizeScheduleTimezone(raw.scheduleTimezone, DEFAULT_WARMUP_DELETION_CONFIG.scheduleTimezone),
    mailboxAgeThresholdDays: normalizePositiveInt(
      raw.mailboxAgeThresholdDays,
      DEFAULT_WARMUP_DELETION_CONFIG.mailboxAgeThresholdDays,
      1,
      365
    ),
    retentionDaysForNewMailbox: normalizePositiveInt(
      raw.retentionDaysForNewMailbox,
      DEFAULT_WARMUP_DELETION_CONFIG.retentionDaysForNewMailbox,
      1,
      365
    ),
    retentionDaysForMatureMailbox: normalizePositiveInt(
      raw.retentionDaysForMatureMailbox,
      DEFAULT_WARMUP_DELETION_CONFIG.retentionDaysForMatureMailbox,
      1,
      365
    ),
    maxConcurrentMailboxes: normalizePositiveInt(
      raw.maxConcurrentMailboxes,
      DEFAULT_WARMUP_DELETION_CONFIG.maxConcurrentMailboxes,
      1,
      10
    ),
    maxMailboxesPerMinute: normalizePositiveInt(
      raw.maxMailboxesPerMinute,
      DEFAULT_WARMUP_DELETION_CONFIG.maxMailboxesPerMinute,
      1,
      120
    ),
    deleteBatchSize: normalizePositiveInt(
      raw.deleteBatchSize,
      DEFAULT_WARMUP_DELETION_CONFIG.deleteBatchSize,
      25,
      500
    ),
    // Sent-by-emailKind path. Strict-true only: any other input
    // (missing, undefined, "true" string, etc.) keeps the path OFF.
    // Bounds for the caps mirror the API's `normalizeWarmupDeletionConfig`
    // exactly so a UI save followed by an API save is a no-op.
    includeSentByEmailKind: raw.includeSentByEmailKind === true,
    sentMaxDeletePerMailboxPerRun: normalizePositiveInt(
      raw.sentMaxDeletePerMailboxPerRun,
      DEFAULT_WARMUP_DELETION_CONFIG.sentMaxDeletePerMailboxPerRun,
      25,
      50_000
    ),
    sentSuspiciousRatioAbort: normalizeRatio(
      raw.sentSuspiciousRatioAbort,
      DEFAULT_WARMUP_DELETION_CONFIG.sentSuspiciousRatioAbort,
      0,
      1
    ),
    sentMinScanForRatioCheck: normalizePositiveInt(
      raw.sentMinScanForRatioCheck,
      DEFAULT_WARMUP_DELETION_CONFIG.sentMinScanForRatioCheck,
      1,
      1_000_000
    ),
  };
}

function normalizeDelayBounceSuppressionConfig(input: unknown): DelayBounceSuppressionConfig {
  const raw = (input && typeof input === 'object')
    ? (input as Record<string, unknown>)
    : {};
  const retryMode = String(raw.retryMode || '').trim().toLowerCase() === 'never'
    ? 'never'
    : 'after_days';
  return {
    enabled: raw.enabled === true,
    suppressAfterDelayBounces24h: normalizePositiveInt(
      raw.suppressAfterDelayBounces24h,
      DEFAULT_DELAY_BOUNCE_SUPPRESSION_CONFIG.suppressAfterDelayBounces24h,
      1,
      10
    ),
    suppressAfterDelayBounces48h: normalizePositiveInt(
      raw.suppressAfterDelayBounces48h,
      DEFAULT_DELAY_BOUNCE_SUPPRESSION_CONFIG.suppressAfterDelayBounces48h,
      1,
      20
    ),
    retryAfterDays: retryMode === 'never' ? 0 : 7,
    retryMode,
  };
}

function normalizeHardBounceSuppressionConfig(input: unknown): HardBounceSuppressionConfig {
  const raw = (input && typeof input === 'object')
    ? (input as Record<string, unknown>)
    : {};
  const scope = String(raw.scope || '').trim().toLowerCase() === 'mailbox' ? 'mailbox' : 'global';
  return {
    enabled: raw.enabled !== false,
    suppressAfterHardBounces: normalizePositiveInt(
      raw.suppressAfterHardBounces,
      DEFAULT_HARD_BOUNCE_SUPPRESSION_CONFIG.suppressAfterHardBounces,
      1,
      5
    ),
    scope,
  };
}

function normalizeBounceClassificationConfig(input: unknown): BounceClassificationConfig {
  const raw = (input && typeof input === 'object')
    ? (input as Record<string, unknown>)
    : {};
  const enabled = raw.enabled === true;
  const effectiveEnabled = enabled && raw.effectiveEnabled === true;
  return {
    enabled,
    activationScope: 'tenant',
    effectiveEnabled,
    v1Enabled: effectiveEnabled,
  };
}

// Phase A (2026-05-13): mirrors API-side
// `src/lib/tenant-warmup-identifier.ts :: normalizeWarmupIdentifierConfig`.
// Strict-true gate on `enabled` matches the existing
// `includeSentByEmailKind` pattern (any non-true keeps it OFF). Pattern
// is trimmed and capped at 256 chars to match the API normalizer
// exactly so a UI save round-trips losslessly. `matchScope`/`matchType`
// fall back to the system defaults when unrecognized.
function sanitizeWarmupIdentifierMatchScope(value: unknown): WarmupIdentifierMatchScope {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'subject' || v === 'body') return v;
  return 'subject_or_body';
}
function sanitizeWarmupIdentifierMatchType(value: unknown): WarmupIdentifierMatchType {
  const v = String(value || '').trim().toLowerCase();
  return v === 'regex' ? 'regex' : 'contains';
}
export function normalizeWarmupIdentifierConfig(input: unknown): WarmupIdentifierConfig {
  const raw = (input && typeof input === 'object')
    ? (input as Record<string, unknown>)
    : {};
  return {
    enabled: raw.enabled === true,
    pattern: String(raw.pattern || '').trim().slice(0, 256),
    matchScope: sanitizeWarmupIdentifierMatchScope(raw.matchScope),
    matchType: sanitizeWarmupIdentifierMatchType(raw.matchType),
    caseSensitive: raw.caseSensitive === true,
  };
}

function normalizeDelayBounceSuppressionSummary(input: unknown): DelayBounceSuppressionSummary | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  const tenantId = String(raw.tenantId || '').trim();
  if (!tenantId) return undefined;
  return {
    tenantId,
    lastSuppressedAt: raw.lastSuppressedAt ? String(raw.lastSuppressedAt) : undefined,
    lastSuppressedEventAt: raw.lastSuppressedEventAt ? String(raw.lastSuppressedEventAt) : undefined,
    lastSuppressedRecipient: raw.lastSuppressedRecipient ? String(raw.lastSuppressedRecipient).toLowerCase().trim() : undefined,
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined,
  };
}

function normalizeWarmupDeletionRunSummary(input: unknown): WarmupDeletionRunSummary | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  return {
    startedAt: raw.startedAt ? String(raw.startedAt) : undefined,
    finishedAt: raw.finishedAt ? String(raw.finishedAt) : undefined,
    status: raw.status ? String(raw.status) as WarmupDeletionRunSummary['status'] : undefined,
    dryRun: raw.dryRun !== false,
    targetFolder: String(raw.targetFolder || DEFAULT_WARMUP_DELETION_CONFIG.targetFolder),
    processedMailboxes: normalizePositiveInt(raw.processedMailboxes, 0, 0, 1_000_000_000),
    totalMailboxes: normalizePositiveInt(raw.totalMailboxes, 0, 0, 1_000_000_000),
    deletedEmails: normalizePositiveInt(raw.deletedEmails, 0, 0, 1_000_000_000),
    candidateEmails: normalizePositiveInt(raw.candidateEmails, 0, 0, 1_000_000_000),
    errors: normalizePositiveInt(raw.errors, 0, 0, 1_000_000_000),
    durationMs: normalizePositiveInt(raw.durationMs, 0, 0, 86_400_000),
    tierBreakdown: normalizeTierBreakdown(raw.tierBreakdown),
    sentByEmailKindBreakdown: normalizeSentByEmailKindBreakdown(raw.sentByEmailKindBreakdown),
    notes: raw.notes ? String(raw.notes) : undefined,
  };
}

function normalizeWarmupDeletionRuntime(input: unknown): WarmupDeletionRuntimeState | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  const runDate = String(raw.runDate || '').trim();
  const startedAt = String(raw.startedAt || '').trim();
  if (!runDate || !startedAt) return undefined;
  return {
    runDate,
    cursor: normalizePositiveInt(raw.cursor, 0, 0, 1_000_000_000),
    totalMailboxes: normalizePositiveInt(raw.totalMailboxes, 0, 0, 1_000_000_000),
    processedMailboxes: normalizePositiveInt(raw.processedMailboxes, 0, 0, 1_000_000_000),
    deletedEmails: normalizePositiveInt(raw.deletedEmails, 0, 0, 1_000_000_000),
    candidateEmails: normalizePositiveInt(raw.candidateEmails, 0, 0, 1_000_000_000),
    errors: normalizePositiveInt(raw.errors, 0, 0, 1_000_000_000),
    startedAt,
    lastHeartbeatAt: raw.lastHeartbeatAt ? String(raw.lastHeartbeatAt) : undefined,
    dryRun: raw.dryRun !== false,
    targetFolder: String(raw.targetFolder || DEFAULT_WARMUP_DELETION_CONFIG.targetFolder),
    status: (String(raw.status || '').toLowerCase() === 'completed') ? 'completed' : 'running',
    tierBreakdown: normalizeTierBreakdown(raw.tierBreakdown),
    sentByEmailKindBreakdown: normalizeSentByEmailKindBreakdown(raw.sentByEmailKindBreakdown),
  };
}


export function normalizeRulesEngineConfig(input: unknown): RulesEngineConfig {
  const raw = (input && typeof input === 'object') ? (input as Record<string, unknown>) : {};
  const accountStages = normalizeStageList(raw.accountStages, DEFAULT_ACCOUNT_STAGES);
  const contactStages = normalizeStageList(raw.contactStages, DEFAULT_CONTACT_STAGES);
  const stageLookup = new Map<string, string>();
  [...accountStages, ...contactStages].forEach((stage) => stageLookup.set(stage.toLowerCase(), stage));

  const rawTriggers = Array.isArray(raw.triggers) ? raw.triggers : DEFAULT_RULES_ENGINE_TRIGGERS;
  const triggers: RulesEngineTrigger[] = [];

  rawTriggers.forEach((candidate, index) => {
    const trigger = (candidate && typeof candidate === 'object')
      ? (candidate as Record<string, unknown>)
      : {};
    const id = String(trigger.id || '').trim() || `trigger-${index + 1}`;
    const name = String(trigger.name || '').trim() || `Trigger ${index + 1}`;
    const enabled = trigger.enabled !== false;
    const target = normalizeTarget(trigger.target);
    const blockedStages = normalizeStageList(trigger.blockedStages, [])
      .map((stage) => stageLookup.get(stage.toLowerCase()) || stage);
    if (blockedStages.length === 0) return;

    triggers.push({
      id,
      name,
      enabled,
      target,
      blockedStages,
    });
  });

  return {
    accountStages,
    contactStages,
    triggers: triggers.length > 0 ? triggers : DEFAULT_RULES_ENGINE_TRIGGERS,
    warmupDeletion: normalizeWarmupDeletionConfig(raw.warmupDeletion),
    // Phase A: round-trip per-tenant Warm-Up Identifier so save/refetch
    // does not silently strip the field. Always returns a populated
    // `WarmupIdentifierConfig` (defaults to disabled/empty).
    warmupIdentifier: normalizeWarmupIdentifierConfig(raw.warmupIdentifier),
    delayBounceSuppression: normalizeDelayBounceSuppressionConfig(raw.delayBounceSuppression),
    hardBounceSuppression: normalizeHardBounceSuppressionConfig(raw.hardBounceSuppression),
    bounceClassification: normalizeBounceClassificationConfig(raw.bounceClassification),
    delayBounceSuppressionSummary: normalizeDelayBounceSuppressionSummary(raw.delayBounceSuppressionSummary),
    warmupDeletionLastRun: normalizeWarmupDeletionRunSummary(raw.warmupDeletionLastRun),
    warmupDeletionRuntime: normalizeWarmupDeletionRuntime(raw.warmupDeletionRuntime),
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined,
    updatedBy: raw.updatedBy ? String(raw.updatedBy) : undefined,
  };
}

/** User-editable fields only — excludes runtime summaries so dirty detection stays stable after save/refetch. */
export type RulesEngineEditableSnapshot = {
  accountStages: string[];
  contactStages: string[];
  triggers: RulesEngineTrigger[];
  warmupDeletion: WarmupDeletionConfig;
  warmupIdentifier: WarmupIdentifierConfig;
  delayBounceSuppression: DelayBounceSuppressionConfig;
  hardBounceSuppression: HardBounceSuppressionConfig;
  bounceClassification: BounceClassificationConfig;
};

export function rulesEngineEditableSnapshot(input: unknown): RulesEngineEditableSnapshot {
  const n = normalizeRulesEngineConfig(input);
  return {
    accountStages: [...n.accountStages],
    contactStages: [...n.contactStages],
    triggers: [...n.triggers].sort((a, b) => a.id.localeCompare(b.id)),
    warmupDeletion: { ...n.warmupDeletion },
    // Phase A: include in dirty-detection snapshot so the Save Rules
    // button activates correctly when the operator edits the
    // identifier pattern or toggles enabled.
    warmupIdentifier: { ...n.warmupIdentifier },
    delayBounceSuppression: { ...n.delayBounceSuppression },
    hardBounceSuppression: { ...n.hardBounceSuppression },
    bounceClassification: { ...n.bounceClassification },
  };
}

export function getHardBounceRuntimeStatus(
  bounceClassification?: BounceClassificationConfig,
  hardBounceSuppression?: HardBounceSuppressionConfig
): HardBounceRuntimeStatus {
  const tenantEnabled = Boolean(bounceClassification?.enabled);
  const tenantActive = Boolean(bounceClassification?.effectiveEnabled);
  const hardPolicyEnabled = hardBounceSuppression?.enabled !== false;

  if (tenantActive && hardPolicyEnabled) return 'active';
  if (tenantEnabled) return 'enabled';
  return 'disabled';
}
